import { endOfDay, isAfter, isBefore, isSameDay, startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import {
  getDueTaskPlanning,
  getTaskPlanning,
} from 'shared/utils/get-task-planning';

export type Options = {
  weekStartsOn: WeekDays;
  today: Timestamp;
};

export const useTaskPlannedOccurrenceForDate = (
  task: Task,
  date: Timestamp,
  { weekStartsOn, today }: Options,
  enabled = true,
) =>
  useMemo(() => {
    if (!enabled) {
      return undefined;
    }

    const planning = getTaskPlanning(task, {
      startDate: startOfDay(date),
      endDate: endOfDay(date),
      today,
      weekStartsOn,
      uncompletedOnly: true,
    });
    const plannedOnDate = planning.find(({ date: _date }) =>
      isSameDay(_date, date),
    );

    if (plannedOnDate) {
      return plannedOnDate;
    }

    // if we display a date in the past, display the first past occurrence
    if (isSameDay(date, today) || isBefore(date, today)) {
      const overduePlanning = getDueTaskPlanning(task, {
        date,
        today,
        weekStartsOn,
        uncompletedOnly: true,
      });

      return overduePlanning[overduePlanning.length - 1];
    }

    // return the first occurrence after the display date
    return planning.find(({ date: _date }) => isAfter(_date, date));
  }, [date, task, today, weekStartsOn, enabled]);
